import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { notify } from '@/lib/notify.js';
import { ensureFreshPlatformJwt, optionalSessionJwt } from '@/lib/sso.js';

import type { AutomationResult } from './automations.js';
import {
  filenameFromContentDisposition,
  ReportFetchError,
} from './insights.js';

/**
 * Client for sharing a scheduled automation beyond its workspace — the automation half of what
 * `lib/api/dashboard-shares.ts` does for saved dashboards, deliberately the same shape and
 * vocabulary. The wire is snake_case both ways (`access_mode`, `subject_type`/`subject_id`,
 * `participant_ids`); the service's ValidationPipe silently drops anything else, so these exact
 * keys are asserted in tests rather than trusted to the types.
 */

/** Who may open a shared automation link: named recipients, or anyone holding the link. */
export type AutomationShareAccessMode = 'named' | 'public';

/** A `named` share's addressee is a colleague or an Entra group, exactly as the dashboard share is. */
export type AutomationShareSubjectType = 'user' | 'entra_group';

/** One recipient a `named` share is addressed to. `displayName` is null when the directory could not resolve the id. */
export interface AutomationShareSubject {
  subjectType: AutomationShareSubjectType;
  subjectId: string;
  displayName: string | null;
}

/** One active or revoked share of an automation, as create and list both return it. */
export interface AutomationShareView {
  id: string;
  automationId: string;
  accessMode: AutomationShareAccessMode;
  /** Owner-chosen label, or null when the link was created without one. */
  name: string | null;
  subjects: AutomationShareSubject[];
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

/** The automation a link resolves to, served without a token by the public serving endpoint. */
export interface SharedAutomation {
  id: string;
  name: string;
  accessMode: AutomationShareAccessMode;
  lastRunAt: string | null;
  results: AutomationResult[];
}

/**
 * What reading a shared link can come back as. `needs-auth` is kept apart from `unavailable`: a 401
 * means the link is fine and the viewer is not signed in, an invitation to sign in rather than a
 * refusal. Everything else unavailable (missing, revoked, expired) collapses into one answer.
 */
export type SharedAutomationResult =
  | { kind: 'ready'; automation: SharedAutomation }
  | { kind: 'needs-auth' }
  | { kind: 'unavailable' };

interface AutomationShareSubjectWire {
  subject_type: AutomationShareSubjectType;
  subject_id: string;
  display_name?: string | null;
}

interface AutomationShareViewWire {
  id: string;
  automation_id: string;
  access_mode: AutomationShareAccessMode;
  name: string | null;
  subjects: AutomationShareSubjectWire[];
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

interface AutomationResultWire {
  id: string;
  created_at: string;
  text: string;
  attachment_count: number;
  report_run_id?: string | null;
}

interface SharedAutomationWire {
  id: string;
  name: string;
  access_mode: AutomationShareAccessMode;
  last_run_at: string | null;
  results: AutomationResultWire[];
}

function shareFromWire(wire: AutomationShareViewWire): AutomationShareView {
  return {
    id: wire.id,
    automationId: wire.automation_id,
    accessMode: wire.access_mode,
    name: wire.name,
    subjects: (wire.subjects ?? []).map((subject) => ({
      subjectType: subject.subject_type,
      subjectId: subject.subject_id,
      displayName: subject.display_name ?? null,
    })),
    expiresAt: wire.expires_at,
    revokedAt: wire.revoked_at,
    createdAt: wire.created_at,
  };
}

/** Platform-JWT bearer (zero-trust) + JSON content type — mirrors `dashboard-shares.ts`'s `authHeaders`. */
async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const jwt = await ensureFreshPlatformJwt();
  if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
  return headers;
}

function sharesEndpoint(automationId: string): string {
  return `/v1/automations/${encodeURIComponent(automationId)}/shares`;
}

async function fetchAutomationShares(
  automationId: string,
): Promise<AutomationShareView[]> {
  const res = await fetch(sharesEndpoint(automationId), {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to load shares (${res.status})`);
  const wire = (await res.json()) as AutomationShareViewWire[];
  return wire.map(shareFromWire);
}

export interface CreateAutomationShareInput {
  accessMode: AutomationShareAccessMode;
  subjects?: { subjectType: AutomationShareSubjectType; subjectId: string }[];
  name?: string;
}

/**
 * Create a share for an automation. `subjects` is sent only for `named` shares — a `public` share
 * addresses nobody, so the body carries no `subjects` key at all.
 */
export async function createAutomationShare(
  automationId: string,
  input: CreateAutomationShareInput,
): Promise<AutomationShareView> {
  const body: Record<string, unknown> = { access_mode: input.accessMode };
  if (input.accessMode === 'named') {
    body.subjects = (input.subjects ?? []).map((subject) => ({
      subject_type: subject.subjectType,
      subject_id: subject.subjectId,
    }));
  }
  if (input.name !== undefined) body.name = input.name;
  const res = await fetch(sharesEndpoint(automationId), {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to create the share (${res.status})`);
  return shareFromWire((await res.json()) as AutomationShareViewWire);
}

export async function revokeAutomationShare(
  automationId: string,
  shareId: string,
): Promise<void> {
  const res = await fetch(
    `${sharesEndpoint(automationId)}/${encodeURIComponent(shareId)}`,
    { method: 'DELETE', headers: await authHeaders() },
  );
  if (!res.ok && res.status !== 204) {
    throw new Error(`Failed to revoke the share (${res.status})`);
  }
}

/**
 * Read the automation behind a share link.
 *
 * Deliberately off the ordinary `authHeaders` path: the bearer comes from `optionalSessionJwt` and is
 * attached only when a session already exists (renewed silently when it aged out), so a viewer with
 * no session never gets dragged through an SSO round-trip just to learn a `named` link needs one. A 401
 * is a valid answer ("sign in to see this"), not a failure to repair — same reasoning as
 * `dashboard-shares.ts`'s `fetchSharedDashboard`.
 */
export async function fetchSharedAutomation(
  shareId: string,
): Promise<SharedAutomationResult> {
  const jwt = await optionalSessionJwt();
  const headers: Record<string, string> = {};
  if (jwt !== null && jwt.length > 0) {
    headers['Authorization'] = `Bearer ${jwt}`;
  }
  const res = await fetch(
    `/v1/automation-shares/${encodeURIComponent(shareId)}`,
    { method: 'GET', headers, cache: 'no-store', credentials: 'omit' },
  );
  if (res.status === 401) return { kind: 'needs-auth' };
  if (!res.ok) return { kind: 'unavailable' };
  const wire = (await res.json()) as SharedAutomationWire;
  return {
    kind: 'ready',
    automation: {
      id: wire.id,
      name: wire.name,
      accessMode: wire.access_mode,
      lastRunAt: wire.last_run_at,
      results: (wire.results ?? []).map((result) => ({
        id: result.id,
        createdAt: result.created_at,
        text: result.text,
        attachmentCount: result.attachment_count,
        reportRunId: result.report_run_id ?? null,
      })),
    },
  };
}

/** The report proxy URL behind a share: the share's own liveness/access check, not the bearer-only one. */
function sharedReportUrl(shareId: string, runId: string): string {
  return `/v1/automation-shares/${encodeURIComponent(shareId)}/reports/${encodeURIComponent(runId)}`;
}

/**
 * Read a shared automation result's HTML report through the share's own serving endpoint.
 *
 * Same bearer rule as {@link fetchSharedAutomation} — attached only when the identity store already
 * holds one, never fetched via an interactive login — because a `public` link's viewer may have no
 * session at all. Mirrors `lib/api/insights.ts`'s `fetchReportHtml` for the error mapping: a
 * `ReportFetchError` distinguishes "not for you" (401/403/404) from a transient failure.
 */
export async function fetchSharedReportHtml(
  shareId: string,
  runId: string,
): Promise<string> {
  const jwt = await optionalSessionJwt();
  const headers: Record<string, string> = { Accept: 'text/html' };
  if (jwt !== null && jwt.length > 0) {
    headers['Authorization'] = `Bearer ${jwt}`;
  }
  const res = await fetch(sharedReportUrl(shareId, runId), {
    method: 'GET',
    headers,
    cache: 'no-store',
    credentials: 'omit',
  });
  if (!res.ok) {
    if (res.status === 401 || res.status === 403 || res.status === 404) {
      throw new ReportFetchError('forbidden');
    }
    throw new ReportFetchError('unavailable');
  }
  return res.text();
}

/** The report-PDF URL behind a share: same route as {@link sharedReportUrl}, plus `format=pdf`. */
function sharedReportPdfUrl(shareId: string, runId: string): string {
  return `${sharedReportUrl(shareId, runId)}?format=pdf`;
}

/**
 * Read a shared automation result's PDF report through the share's own serving endpoint, as a
 * `File` named from the response's `Content-Disposition` header (falling back to
 * `insights-report-<runId>.pdf`). Same bearer rule and error mapping as
 * {@link fetchSharedReportHtml} — a `ReportFetchError` distinguishes "not for you" (401/403/404)
 * from a transient failure.
 */
export async function fetchSharedReportPdf(
  shareId: string,
  runId: string,
): Promise<Blob> {
  const jwt = await optionalSessionJwt();
  const headers: Record<string, string> = { Accept: 'application/pdf' };
  if (jwt !== null && jwt.length > 0) {
    headers['Authorization'] = `Bearer ${jwt}`;
  }
  const res = await fetch(sharedReportPdfUrl(shareId, runId), {
    method: 'GET',
    headers,
    cache: 'no-store',
    credentials: 'omit',
  });
  if (!res.ok) {
    if (res.status === 404) throw new ReportFetchError('missing');
    if (res.status === 401 || res.status === 403) {
      throw new ReportFetchError('forbidden');
    }
    throw new ReportFetchError('unavailable');
  }
  const bytes = await res.blob();
  const filename = filenameFromContentDisposition(
    res,
    `insights-report-${runId}.pdf`,
  );
  return new File([bytes], filename, { type: 'application/pdf' });
}

/** TanStack Query factory for a shared result's HTML report; reports are immutable once generated. */
export function sharedReportQueryOptions(shareId: string, runId: string) {
  return queryOptions({
    queryKey: ['shared-automation-report', shareId, runId],
    queryFn: () => fetchSharedReportHtml(shareId, runId),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Query keys for an automation's shares. */
export const automationShareKeys = {
  list: (automationId: string) => ['automation-shares', automationId] as const,
};

/** TanStack Query factory for an automation's shares; answers an empty list with no automation in hand. */
export function automationSharesQueryOptions(automationId: string | null) {
  return queryOptions({
    queryKey: automationShareKeys.list(automationId ?? 'none'),
    queryFn: async (): Promise<AutomationShareView[]> => {
      if (!automationId) return [];
      return fetchAutomationShares(automationId);
    },
    enabled: Boolean(automationId),
    staleTime: 30_000,
  });
}

/** The shares that still open, in the order the service listed them: not revoked, not expired. */
export function activeAutomationShares(
  shares: readonly AutomationShareView[],
): AutomationShareView[] {
  const now = Date.now();
  return shares.filter((share) => {
    if (share.revokedAt !== null) return false;
    if (share.expiresAt === null) return true;
    return Date.parse(share.expiresAt) > now;
  });
}

/** The user-facing share URL — an SPA route, not the `/v1` serving endpoint. */
export function automationShareUrl(shareId: string): string {
  return `${window.location.origin}/scheduled/shared/${encodeURIComponent(shareId)}`;
}

interface CreateAutomationShareVars extends CreateAutomationShareInput {
  automationId: string;
}

export function useCreateAutomationShare(): UseMutationResult<
  AutomationShareView,
  Error,
  CreateAutomationShareVars
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ automationId, ...input }: CreateAutomationShareVars) =>
      createAutomationShare(automationId, input),
    onSuccess: (_share, { automationId }) => {
      void queryClient.invalidateQueries({
        queryKey: automationShareKeys.list(automationId),
      });
    },
    onError: () => notify.error("Couldn't create the link"),
  });
}

interface RevokeAutomationShareVars {
  automationId: string;
  shareId: string;
}

export function useRevokeAutomationShare(): UseMutationResult<
  void,
  Error,
  RevokeAutomationShareVars
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ automationId, shareId }: RevokeAutomationShareVars) =>
      revokeAutomationShare(automationId, shareId),
    onSuccess: (_data, { automationId }) => {
      void queryClient.invalidateQueries({
        queryKey: automationShareKeys.list(automationId),
      });
    },
    onError: () => notify.error("Couldn't revoke the link"),
  });
}
